import axios from 'axios';
import { baseUrlAccount, baseUrlTransactions } from '../context/BaseUrl';

// Unified Base URL through Gateway
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const GATEWAY_URL = `${API_BASE}/api/`;

// Instance axios avec config pointing to gateway
const api = axios.create({
  baseURL: GATEWAY_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Intercepteur pour ajouter le token à chaque requête (using standard accessToken key)
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Fallback to old key just in case, but accessToken is primary
      const authTokens = localStorage.getItem('authTokens');
      if (authTokens) {
        try {
          const tokens = JSON.parse(authTokens);
          config.headers.Authorization = `Bearer ${tokens.access || tokens.accessToken}`;
        } catch (e) { }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Service API pour Bousanm Solidè
export const bousanmSolideApi = {
  /**
   * Récupérer les statistiques globales
   */
  getGlobalStats: async () => {
    try {
      const response = await api.get('dashboard/statistiques-globales/');
      return response.data;
    } catch (error) {
      console.error('Erreur getGlobalStats:', error);
      throw error;
    }
  },

  /**
   * Récupérer le portfolio des agents
   * @param {Object} filters - Filtres (page, page_size, agent, plan, sort_by, etc.)
   */
  getAgentsPortfolio: async (filters = {}) => {
    try {
      const response = await api.get('dashboard/agents-portfolio/', {
        params: {
          page: filters.page || 1,
          page_size: filters.page_size || 20,
          ...filters
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur getAgentsPortfolio:', error);
      throw error;
    }
  },

  /**
   * Récupérer les transactions d'un agent
   * @param {string} username - Username de l'agent
   * @param {Object} filters - Filtres (date_debut, date_fin, client_code, etc.)
   */
  getAgentTransactions: async (username, filters = {}) => {
    try {
      const response = await api.get(`dashboard/transactions-agent/${username}/`, {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Erreur getAgentTransactions:', error);
      throw error;
    }
  },

  /**
   * Récupérer l'historique des assignations
   * @param {Object} filters - Filtres optionnels
   */
  getAssignationsHistory: async (filters = {}) => {
    try {
      const response = await api.get('agent-assignment/history/', {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Erreur getAssignationsHistory:', error);
      throw error;
    }
  },

  /**
   * Récupérer les rapports globaux (Monitoring)
   */
  getRapportsSolide: async (filters = {}) => {
    try {
      const response = await api.get('dashboard/rapports-solide/', {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Erreur getRapportsSolide:', error);
      throw error;
    }
  },

  /**
   * Télécharger le rapport Excel
   */
  downloadRapportExcel: async (filters = {}) => {
    try {
      const response = await api.get('dashboard/export-rapport-excel/', {
        params: filters,
        responseType: 'blob' // Important pour les fichiers binaires
      });
      return response.data;
    } catch (error) {
      console.error('Erreur downloadRapportExcel:', error);
      throw error;
    }
  },

  /**
   * Récupérer la liste de tous les agents (pour selecteurs)
   */
  getAllAgentsPortfolio: async () => {
    try {
      const response = await api.get('app/tous-les-agents-portefeuille/');
      return response.data;
    } catch (error) {
      console.error('Erreur getAllAgentsPortfolio:', error);
      throw error;
    }
  },

  /**
   * Récupérer la liste simple des agents (pour assignation)
   */
  getAgentsList: async () => {
    try {
      const response = await api.get('app/agents-list/');
      return response.data;
    } catch (error) {
      console.error('Erreur getAgentsList:', error);
      throw error;
    }
  },

  /**
   * Récupérer TOUS les clients avec plan Solide actif (ADMIN)
   */
  getAllClientsSolide: async () => {
    try {
      const response = await api.get('dashboard/all-clients-solide/');
      return response.data;
    } catch (error) {
      console.error('Erreur getAllClientsSolide:', error);
      throw error;
    }
  },

  /**
   * Récupérer les transactions Solide avec filtres avancés
   * @param {Object} filters - { page, search, date_debut, date_fin, agent, montant_min, montant_max }
   */
  getTransactionsSolide: async (filters = {}) => {
    try {
      const response = await api.get('dashboard/transactions-solide/', {
        params: {
          page: filters.page || 1,
          page_size: filters.page_size || 20,
          ...filters
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur getTransactionsSolide:', error);
      throw error;
    }
  },

  /**
   * Récupérer les statistiques par plan
   */
  getPlansStats: async () => {
    try {
      const response = await api.get('dashboard/plans-stats/');
      return response.data;
    } catch (error) {
      console.error('Erreur getPlansStats:', error);
      throw error;
    }
  },

  // --- GESTION DES PLANS (CONFIGURATION) ---

  /**
   * Récupérer la configuration de tous les plans
   */
  getPlansConfiguration: async () => {
    try {
      const response = await api.get('dashboard/plans-configuration/');
      return response.data;
    } catch (error) {
      console.error('Erreur getPlansConfiguration:', error);
      throw error;
    }
  },

  /**
   * Créer un nouveau plan
   * @param {Object} data - Données du plan
   */
  createPlan: async (data) => {
    try {
      const response = await api.post('dashboard/plans-configuration/', data);
      return response.data;
    } catch (error) {
      console.error('Erreur createPlan:', error);
      throw error;
    }
  },

  /**
   * Mettre à jour un plan
   * @param {string} id - ID du plan (UUID)
   * @param {Object} data - Données à mettre à jour
   */
  updatePlan: async (id, data) => {
    try {
      const response = await api.put(`dashboard/plans-configuration/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error('Erreur updatePlan:', error);
      throw error;
    }
  },

  /**
   * Supprimer un plan
   * @param {string} id - ID du plan (UUID)
   */
  deletePlan: async (id) => {
    try {
      const response = await api.delete(`dashboard/plans-configuration/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Erreur deletePlan:', error);
      throw error;
    }
  },

  // --- GESTION CONFIGURATION (TAUX, ETC.) ---

  /**
   * Récupérer le taux de change actuel
   */
  getTauxChange: async () => {
    try {
      const response = await api.get('dashboard/configuration-taux/');
      return response.data;
    } catch (error) {
      console.error('Erreur getTauxChange:', error);
      throw error;
    }
  },

  /**
   * Mettre à jour le taux de change
   * @param {number} taux - Nouveau taux
   */
  updateTauxChange: async (taux) => {
    try {
      const response = await api.post('dashboard/configuration-taux/', { taux });
      return response.data;
    } catch (error) {
      console.error('Erreur updateTauxChange:', error);
      throw error;
    }
  },

  // --- GESTION ASSIGNATIONS ---

  /**
   * Rechercher un compte pour assignation
   * @param {string} code - Code du compte
   */
  getCompteAgent: async (code) => {
    try {
      const response = await api.get(`dashboard/compte-lookup/?code=${code}`);
      return response.data;
    } catch (error) {
      console.error('Erreur getCompteAgent:', error);
      throw error;
    }
  },

  /**
   * Assigner un agent à un compte
   * @param {Object} data - { compte_code, agent_username, raison }
   */
  assignAgent: async (data) => {
    try {
      const response = await api.post('dashboard/assign-agent/', data);
      return response.data;
    } catch (error) {
      console.error('Erreur assignAgent:', error);
      throw error;
    }
  },

  /**
   * Désassigner un agent
   * @param {string} compte_code 
   */
  unassignAgent: async (compte_code) => {
    try {
      const response = await api.post('dashboard/unassign-agent/', { compte_code });
      return response.data;
    } catch (error) {
      console.error('Erreur unassignAgent:', error);
      throw error;
    }
  },

  /**
   * Rechercher des comptes (online)
   * @param {string} searchTerm 
   * @param {Object} filters - Filtres optionnels { type_compte, etat }
   */
  searchAccounts: async (searchTerm, filters = {}) => {
    try {
      const response = await api.post('account/search/', {
        search_term: searchTerm,
        ...filters
      });
      return response.data;
    } catch (error) {
      console.error('Erreur searchAccounts:', error);
      throw error;
    }
  },

  /**
   * Obtenir les infos pour un dépôt solidaire (vérif premier dépôt)
   * @param {string} compteCode 
   */
  getDepoSolidaireInfo: async (compteCode) => {
    try {
      const response = await api.get(`depo-solidaire/info/?compte_code=${compteCode}`, {
        baseURL: baseUrlTransactions
      });
      return response.data;
    } catch (error) {
      console.error('Erreur getDepoSolidaireInfo:', error);
      throw error;
    }
  },

  /**
   * Effectuer un dépôt solidaire
   * @param {Object} data - { compte_code, plan_sol, nb_semaines }
   */
  createDepoSolidaire: async (data) => {
    try {
      const response = await api.post('depo-solidaire/', data, {
        baseURL: baseUrlTransactions
      });
      return response.data;
    } catch (error) {
      console.error('Erreur createDepoSolidaire:', error);
      throw error;
    }
  },
};

export default bousanmSolideApi;
